<template>
  <form ref="form" class="mt-3" :class="{'was-validated': formWasValidated}">
    <div class="form-group">
      <input class="form-control form-control-lg" placeholder="Titre" :disabled="!isModificationEnabled" v-model="internalSong.title" required />
      <div class="invalid-feedback">
        Veuillez indiquer le titre du chant.
      </div>
    </div>
    <div class="form-group">
      <input class="form-control form-control-sm" placeholder="Auteur(s)" :disabled="!isModificationEnabled" v-model="internalSong.author" />
    </div>
    <div class="form-group">
      <textarea class="form-control lyrics" placeholder="Paroles" :disabled="!isModificationEnabled" v-model="internalSong.lyrics" required></textarea>
      <div class="invalid-feedback">
        Veuillez rentrer les paroles du chant.
      </div>
    </div>
    <!--
    Tempo: <input v-model="internalSong.tempo" />
    Tonalité: <input v-model="internalSong.key" />
    -->

    <div class="row" v-if="isModificationEnabled">
      <div class="col-md-6">

        <div class="alert alert-info" role="alert">
          Vous pouvez ajouter au maximum un fichier de chaque parmi les types de fichier suivants:
          <ul>
            <li>Un fichier PDF pour les paroles et accords</li>
            <li>Un fichier audio mp3</li>
            <li>Un fichier texte avec uniquement les paroles du chant (ou les écrire directement dans le champ 'paroles' ci-dessus)</li>
            <li>Un fichier ChordPro (.chopro)</li>
          </ul>
        </div>

      </div>
      <div class="col-md-6">

        <FileUpload
          v-model="fileUploads"
          :allowed-extensions="['pdf','mp3','txt','chopro']"
          :fileBaseName="fileBaseName"
          @error="handleFileUploadError"
          @success="handleFileUploadSuccess" />

      </div>
    </div>

    <FileList :isModificationEnabled="isModificationEnabled" :file-uploads="fileUploads" :files="files" @delete="deleteFile" />

    <div class="form-group">
      <input class="btn btn-primary mr-2" :disabled="!isModificationEnabled" type="submit" value="Enregistrer" @click.prevent="submit()" />
      <button class="btn btn-secondary" @click.prevent="$router.back()">Annuler</button>
    </div>
  </form>
</template>

<style scoped>
  form {
    text-align: left;
  }
  textarea.lyrics {
    height: 300px;
  }
</style>

<script>
  import FileUpload from "@/components/FileUpload";
  import FileList from "@/components/FileList";
  import { mapGetters, mapActions } from "vuex";
  import uuid from "uuid";
  import axios from "axios";
  import { REST_ENDPOINT } from "@/constants";

  function getRestEndpointForFile(fileBaseName, file) {
    return `${REST_ENDPOINT}upload/${fileBaseName}.${file.name.split('.').pop()}`;
  }

  function getFileType(file) {
    return file.type || file.name.split('.').pop();
  }

  export {
    getRestEndpointForFile,
    getFileType
  };

  export default {
    components: {
      FileUpload,
      FileList
    },
    props: {
      song: {
        type: Object,
        default: () => {}
      }
    },
    data() {
      return {
        internalSong: {},
        fileUploads: [],
        files: [],
        fileBaseName: uuid.v4(),
        formWasValidated: false,
        isModificationEnabled: true,
        isUnmounted: false
      };
    },
    watch: {
      song() {
        if (this.song)
          this.copyToInternalSong(this.song);
      }
    },
    mounted() {
      if (this.song)
        this.copyToInternalSong(this.song);
    },
    beforeUnmount() {
      this.isUnmounted = true;
    },
    computed: {
      ...mapGetters(["authToken"]),
    },
    methods: {
      ...mapActions(['loadSongs']),
      copyToInternalSong(song) {
        axios
          .post(`${REST_ENDPOINT}start-modifying-song.php`, { fileBaseName: this.fileBaseName, ...song }, { headers: { 'X-Token': this.authToken } })
          .catch(err => {

            let errors = (err?.response?.data?.errors || []).join(', ');

            this.showToast({
              title: 'Échec',
              text: `Une erreur est survenue à l'ouverture du chant. ${errors}.`,
              type: 'error',
              onDismiss: () => {
                if (!this.isUnmounted)
                  this.$router.back();
              }
            });
            this.isModificationEnabled = false;
          });
        this.internalSong = {...song};
        for (var i in this.song.files) {
          this.files.push(this.song.files[i]);
        }
      },
      submit() {
        this.formWasValidated = true;
        if (!this.$refs.form.checkValidity())
          return;

        this.internalSong.fileBaseName = this.fileBaseName;

        const method = this.internalSong.id ? 'put' : 'post';

        axios[method](`${REST_ENDPOINT}save-song.php`, this.internalSong, { headers: { 'X-Token': this.authToken } })
          .then(() => {
            this.loadSongs();
            this.$router.push({name: 'home'});
          })
          .catch(error => { 
            console.log(error);
            console.log(error.response.data);
            console.log(error.response.status);
            console.log(error.response.headers);
            this.showToast({
              title: 'Échec',
              text: 'Une erreur est survenue, impossible d\'enregistrer.',
              type: 'error'
            });
          });

      },
      deleteFile(file) {
        axios
          .delete(getRestEndpointForFile(this.fileBaseName, file), { headers: { 'X-Token': this.authToken }})
          .then(() => {
            this.removeFileFromList(file);
          })
          .catch(() => {
            this.showToast({
              title: 'Échec de la suppression',
              text: 'Une erreur est survenue lors de la suppression du fichier',
              type: 'error'
            });
          });
      },
      removeFileFromList(file) {
        const fileType = getFileType(file);
        const f = this.files.filter(f => fileType === getFileType(f))[0];
        const i = this.files.indexOf(f);
        this.files.splice(i, 1);
      },
      handleFileUploadSuccess(file) {
        if (!file.response)
          return;

        let files = JSON.parse(file.response);
        if (!Array.isArray(files)) 
          return;

        let fileTypes = this.files.map(getFileType);
        files.forEach(f => {
          if (fileTypes.indexOf(getFileType(f)) == -1)
            this.files.push(f);
        });

        fileTypes = files.map(getFileType);
        let removeFiles = [];
        this.files.forEach(f => {
          if (fileTypes.indexOf(getFileType(f)) == -1)
            removeFiles.push(f);
        });
        removeFiles.forEach(this.removeFileFromList);

        if (file.type == "text/plain")
        {
          var reader = new FileReader();
          reader.readAsText(file.file, "UTF-8");
          reader.onload = evt => {
            this.internalSong.lyrics = evt.target.result;
          };
        }
      },
      handleFileUploadError(file) {
        let errorMessage = '';
        switch(file.error) {
          case 'extension':
            errorMessage = 'Ce type de fichier n\'est pas accepté. Seuls les fichiers pdf, mp3, txt et chopro sont acceptés.'
            break;
          case 'server':
            errorMessage = 'Une erreur est survenue sur le serveur.';
            break;
          case 'network':
            errorMessage = 'Une erreur de réseau est survenue.';
            break;
          default:
            errorMessage = `Une erreur inconnue est survenue (${file.error}).`;
            break;
        }
        this.showToast({
          title: "Échec de l'envoi",
          text: `L'envoi du fichier '${file.name}' a échoué. ` + errorMessage,
          type: 'error',
          onDismiss: () => this.removeFileFromList(file)
        });
      }
    },
    inject: ['showToast']
  }
</script>